/**
 * Weekly review derivation (`docs/specs/phase-2c-weekly-review.md` WS1) --
 * `getWeeklyReview`/`getCurrentReviewWeek`'s real implementation. Composes:
 * - Per-habit planned-vs-actual + 4-week trend (`../habits/logging.ts`/
 *   `../habits/week.ts`, ADR-0006).
 * - Habit-realism suggestions (`../realism/habit-realism.ts`, ADR-0010 --
 *   a separate module, never merged with objective forecast math).
 * - Objective status: touched-this-week flag, checkpoint answer, and a
 *   READ-ONLY re-display of Phase 1's unchanged `computeObjectiveForecast`
 *   (`../forecast/objective-forecast.ts` -- WR-13, zero new math there).
 *
 * Pure function of (event log, today) -- no I/O, no stored per-week
 * counters (AC-W-S3).
 *
 * Phase 5 additions (ADR-0002 addendum 2026-07-04 D7): `draft`-state
 * objectives are excluded entirely (never appear in `WeeklyReviewView
 * .objectives`, regardless of the `includeInWeeklyReview` toggle); `active`
 * objectives additionally respect their own `includeInWeeklyReview` toggle;
 * `on_hold` objectives are ALWAYS included (never gated by the toggle) but
 * carry `status: 'on_hold'` on `ObjectiveReviewItem` so the UI can group
 * them into their own "resume or archive?" section, never mixed into the
 * active pace-review list (AC-O8).
 */

import type {
  CompassEvent,
  HabitId,
  HabitWeekReport,
  ObjectiveCheckpointAnswer,
  ObjectiveDateSuggestionView,
  ObjectiveId,
  ObjectiveReviewItem,
  WeeklyReviewView,
  WeekOutcome,
  WeekStart,
} from '../../contracts';
import { computeObjectiveForecast } from '../forecast/objective-forecast';
import { computeWeekOutcome } from '../habits/logging';
import { buildHabitStates, weeklyPlannedFor } from '../habits/state';
import { addDaysToDate, isInWeek, weekOf, weekSunday } from '../habits/week';
import { computeActionCompletionStatus, buildObjectiveStates } from '../objectives/state';
import { eventsOfTypes, excludingReverted } from '../event-log/event-log';
import { computeHabitRealismSuggestion } from '../realism/habit-realism';

const TREND_WEEKS = 4;
const WEEK_DAYS = 7;

// ---------------------------------------------------------------------------
// getCurrentReviewWeek (WR-6: oldest incomplete week first)
// ---------------------------------------------------------------------------

/** Every distinct weekStart with an active `review.completed` event. */
function completedReviewWeeks(events: readonly CompassEvent[]): ReadonlySet<WeekStart> {
  const active = excludingReverted(events);
  return new Set(eventsOfTypes(active, ['review.completed']).map((e) => e.weekStart));
}

/**
 * The earliest week that has real activity (a habit/objective/checkin event)
 * -- the floor for "oldest incomplete week", so a brand-new log doesn't walk
 * back to the epoch. Falls back to `today`'s week when the log has no
 * activity yet (AC-W1-adjacent: a fresh install's current review week is
 * simply this week).
 */
function earliestActivityWeek(events: readonly CompassEvent[], today: string): WeekStart {
  let earliest: string | null = null;
  for (const event of events) {
    if (earliest === null || event.occurredOn < earliest) {
      earliest = event.occurredOn;
    }
  }
  return weekOf(earliest ?? today);
}

/**
 * WR-1/WR-6: the review week the UI should present now. Walks forward from
 * the earliest week with activity, returning the first week whose Sunday has
 * passed (or is today) and which has no completed review yet. If every past
 * week is already reviewed, returns the current (possibly still-open) week.
 */
export function computeCurrentReviewWeek(
  events: readonly CompassEvent[],
  today: string,
): WeekStart {
  const completed = completedReviewWeeks(events);
  const currentWeek = weekOf(today);
  const floor = earliestActivityWeek(events, today);

  let cursor = floor;
  // Bounded walk: at most the number of weeks between floor and currentWeek
  // (inclusive), so this terminates even on a log spanning years.
  while (cursor < currentWeek) {
    if (weekSunday(cursor) <= today && !completed.has(cursor)) {
      return cursor;
    }
    cursor = weekOf(addDaysToDate(cursor, WEEK_DAYS));
  }
  return currentWeek;
}

// ---------------------------------------------------------------------------
// Per-habit report (WR-2/WR-3, AC-W4-W7)
// ---------------------------------------------------------------------------

/**
 * Up to `TREND_WEEKS` prior CLOSED weeks, oldest first, stopping at
 * whichever is fewer: the trend window or the habit's own creation week
 * (AC-W5: "never padded with synthetic zero-weeks"). `beforeWeek` is
 * exclusive -- the review's own current week is reported separately as
 * `HabitWeekReport.current`, never duplicated into `trend`.
 */
function habitTrend(
  events: readonly CompassEvent[],
  habitId: HabitId,
  beforeWeek: WeekStart,
  kind: 'build' | 'avoid',
  planned: number,
  createdOn: string,
): readonly WeekOutcome[] {
  const createdWeek = weekOf(createdOn);
  const weeks: WeekStart[] = [];
  let cursor = beforeWeek;
  for (let i = 0; i < TREND_WEEKS; i += 1) {
    cursor = weekOf(addDaysToDate(cursor, -WEEK_DAYS));
    if (cursor < createdWeek) break;
    weeks.push(cursor);
  }
  weeks.reverse(); // oldest first
  return weeks.map((w) => computeWeekOutcome(events, habitId, w, kind, planned));
}

function buildHabitWeekReports(
  events: readonly CompassEvent[],
  weekStart: WeekStart,
): readonly HabitWeekReport[] {
  const states = buildHabitStates(events);
  const reports: HabitWeekReport[] = [];

  for (const state of states.values()) {
    if (state.lifecycle !== 'active') continue; // WR-2: paused excluded, archived excluded

    const kind = state.plan.habitKind;
    const planned = weeklyPlannedFor(state.plan);

    const current = computeWeekOutcome(events, state.habitId, weekStart, kind, planned);
    const trend = habitTrend(events, state.habitId, weekStart, kind, planned, state.createdOn);
    const suggestion = computeHabitRealismSuggestion(events, state.habitId, weekStart);

    reports.push({
      habitId: state.habitId,
      name: state.name,
      category: state.category,
      habitKind: kind,
      current,
      trend,
      // WR-3: layout reservation only -- no study-session log producer
      // exists yet (Phase 3), so this is always null this round regardless
      // of category (AC-W7).
      minutesLogged: null,
      suggestion,
    });
  }

  return reports.sort((a, b) => (a.habitId < b.habitId ? -1 : a.habitId > b.habitId ? 1 : 0));
}

// ---------------------------------------------------------------------------
// Per-objective review item (WR-12/WR-13/WR-14, AC-W14-W18)
// ---------------------------------------------------------------------------

/** True when an `action.completed` or any action-plan-edit event for this objective landed inside `weekStart` (WR-12). */
function touchedThisWeek(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  weekStart: WeekStart,
): boolean {
  const active = excludingReverted(events);
  const relevantTypes = ['action.completed', 'action.added', 'action.edited', 'action.removed', 'actions.reordered'] as const;
  return eventsOfTypes(active, relevantTypes).some(
    (e) => e.objectiveId === objectiveId && isInWeek(e.occurredOn, weekStart),
  );
}

/** The latest (canonical-order) checkpoint answer recorded for this objective in this review week, or null. */
function checkpointAnswerFor(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  weekStart: WeekStart,
): ObjectiveCheckpointAnswer | null {
  const active = excludingReverted(events);
  const answers = eventsOfTypes(active, ['objective.checkpoint_answered']).filter(
    (e) => e.objectiveId === objectiveId && e.weekStart === weekStart,
  );
  const last = answers[answers.length - 1];
  return last ? last.answer : null;
}

function buildObjectiveReviewItems(
  events: readonly CompassEvent[],
  weekStart: WeekStart,
  today: string,
): readonly ObjectiveReviewItem[] {
  const states = buildObjectiveStates(events);
  const completion = computeActionCompletionStatus(events);
  const items: ObjectiveReviewItem[] = [];

  for (const state of states.values()) {
    // Phase 5 (D7): draft is excluded entirely, regardless of the toggle;
    // archived stays excluded as before. active additionally respects its
    // own includeInWeeklyReview toggle; on_hold is ALWAYS included (never
    // toggle-gated) so it can surface in its own "resume or archive?"
    // grouping (AC-O8).
    if (state.status === 'draft' || state.status === 'archived') continue;
    if (state.status === 'active' && !state.includeInWeeklyReview) continue;

    const activeActions = [...state.actionsById.values()].filter((a) => !a.removed);
    const actionsDone = activeActions.filter((a) => completion.has(a.actionId)).length;
    const actionsTotal = activeActions.length;

    const forecast = computeObjectiveForecast(events, state.objectiveId, today);
    const dateSuggestion = buildDateSuggestion(state.objectiveId, state.targetDate, forecast, weekStart);

    items.push({
      objectiveId: state.objectiveId,
      title: state.title,
      actionsDone,
      actionsTotal,
      touchedThisWeek: touchedThisWeek(events, state.objectiveId, weekStart),
      checkpointAnswer: checkpointAnswerFor(events, state.objectiveId, weekStart),
      forecast,
      dateSuggestion,
      status: state.status,
    });
  }

  return items.sort((a, b) => (a.objectiveId < b.objectiveId ? -1 : a.objectiveId > b.objectiveId ? 1 : 0));
}

/** WR-13: mirrors the accept payload Phase 1's ObjectiveDetailScreen already builds from the same forecast shape. */
function buildDateSuggestion(
  objectiveId: ObjectiveId,
  currentTargetDate: string,
  forecast: ReturnType<typeof computeObjectiveForecast>,
  weekStart: WeekStart,
): ObjectiveDateSuggestionView | null {
  if (!forecast.isSlipping || !forecast.suggestion) return null;
  return {
    objectiveId,
    currentTargetDate: currentTargetDate as ObjectiveDateSuggestionView['currentTargetDate'],
    suggestedTargetDate: forecast.suggestion.suggestedTargetDate,
    requiredPaceActionsPerWeek: forecast.suggestion.requiredPaceActionsPerWeek,
    reviewWeek: weekStart,
  };
}

// ---------------------------------------------------------------------------
// getWeeklyReview (WR-1/WR-4, AC-W1-W3)
// ---------------------------------------------------------------------------

export function computeWeeklyReview(
  events: readonly CompassEvent[],
  weekStart: WeekStart,
  today: string,
): WeeklyReviewView {
  const available = weekSunday(weekStart) <= today;
  const completed = completedReviewWeeks(events).has(weekStart);

  return {
    weekStart,
    available,
    completed,
    habits: buildHabitWeekReports(events, weekStart),
    objectives: buildObjectiveReviewItems(events, weekStart, today),
  };
}

// Re-exported for the weekly-target-bonus computation (rewards wiring) and
// for tests that need the same closed-week signal this module derives.
// `computeWeekOutcome` itself now lives in `../habits/logging.ts` (phase-3
// spec §2 HL-2: shared by `ObjectiveView.linkedHabits`' current-week
// adherence too) -- re-exported here under its original name so existing
// external imports of `computeWeekOutcome` from this module keep working.
export { computeWeekOutcome } from '../habits/logging';
